import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { colors } from '../../../theme';

export interface FAQItemProps {
  question: string;
  answer: string;
  /** Control expanded state externally */
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  /** Visual variant */
  variant?: 'default' | 'card';
}

/**
 * FAQItem – expandable accordion item.
 *
 * Matches the FAQ Module Figma design:
 * Light blue (#e8f4fd) background, primary blue accent,
 * + / − icon toggle.
 */
export const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  expanded: controlledExpanded,
  onChange,
  variant = 'default',
}) => {
  const [internalExpanded, setInternalExpanded] = React.useState(false);
  const isExpanded = controlledExpanded ?? internalExpanded;

  const handleChange = (_: React.SyntheticEvent, newExpanded: boolean) => {
    setInternalExpanded(newExpanded);
    onChange?.(newExpanded);
  };

  const isCard = variant === 'card';

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: '#e8f4fd',
        border: `1px solid #dde8f0`,
        borderRadius: '12px !important',
        mb: isCard ? 0 : 1.5,
        overflow: 'hidden',
        '&::before': { display: 'none' }, // remove MUI default top border
        '&.Mui-expanded': {
          borderColor: colors.primary.light,
          boxShadow: `0 2px 12px rgba(74,144,226,0.12)`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          isExpanded ? (
            <RemoveRoundedIcon
              sx={{
                fontSize: 20,
                color: '#fff',
                backgroundColor: colors.primary.main,
                borderRadius: '50%',
                p: '2px',
              }}
            />
          ) : (
            <AddRoundedIcon
              sx={{
                fontSize: 20,
                color: colors.primary.main,
                backgroundColor: colors.primary.lightest,
                borderRadius: '50%',
                p: '2px',
              }}
            />
          )
        }
        sx={{
          px: 2.5,
          py: 0.5,
          minHeight: 56,
          '& .MuiAccordionSummary-content': { my: 1.5 },
          '&:hover': { backgroundColor: 'rgba(74,144,226,0.05)' },
        }}
      >
        <Typography
          variant="body1"
          fontWeight={600}
          sx={{ pr: 1, color: isExpanded ? colors.primary.dark : colors.text.primary }}
        >
          {question}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: 2.5,
          pb: 2.5,
          pt: 0,
          borderTop: `1px solid #dde8f0`,
        }}
      >
        <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
          {answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export interface FAQListProps {
  items: Array<{ question: string; answer: string }>;
  /** Allow multiple items open at same time */
  allowMultiple?: boolean;
}

/**
 * FAQList – renders a list of FAQItems, with optional single-expand behaviour.
 */
export const FAQList: React.FC<FAQListProps> = ({ items, allowMultiple = false }) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  return (
    <>
      {items.map((item, i) => (
        <FAQItem
          key={i}
          question={item.question}
          answer={item.answer}
          expanded={allowMultiple ? undefined : openIndex === i}
          onChange={allowMultiple ? undefined : (expanded) => setOpenIndex(expanded ? i : null)}
        />
      ))}
    </>
  );
};

export default FAQItem;
